import { type GetCustomMetrics_me_customMetrics } from "@/nb-api-graphql-generated";
import { type AbstractUser } from "@/user";
import {
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";

import { type CustomGoalRow } from "../metrics/metrics";

import {
  makeGoogleAllConversions,
  makeGoogleAllConversionsValue,
  makeGoogleAverageCPC,
  makeGoogleCAC,
  makeGoogleClicks,
  makeGoogleConversions,
  makeGoogleConversionsValue,
  makeGoogleCostPerConversion,
  makeGoogleImpressions,
  makeGoogleROAS,
  makeGoogleVideoViews,
  makeGoogleViewThroughConversions,
} from "./google";
import {
  makeMeta3SVideoViews1DClick,
  makeMeta3SVideoViews7DClick,
  makeMeta3SVideoViews7DClick1DView,
  makeMeta3SVideoViewsDefault,
  makeMetaAOV1DClick,
  makeMetaAOV1DView,
  makeMetaAOV7DClick,
  makeMetaAOV7DClick1DView,
  makeMetaCAC1DClick,
  makeMetaCAC1DView,
  makeMetaCAC7DClick,
  makeMetaCAC7DClick1DView,
  makeMetaCacWithMetaShopTransactionsIncluded1DClick,
  makeMetaCacWithMetaShopTransactionsIncluded1DView,
  makeMetaCacWithMetaShopTransactionsIncluded7DClick,
  makeMetaCacWithMetaShopTransactionsIncluded7DClick1DView,
  makeMetaCostPer3SecondVideoPlays,
  makeMetaCostPerPageEngagement1DClick,
  makeMetaCostPerPageEngagement1DView,
  makeMetaCostPerPageEngagement7DClick,
  makeMetaCostPerPageEngagement7DClick1DView,
  makeMetaCostPerPageEngagementDefault,
  makeMetaCostPerPageLikes1DClick,
  makeMetaCostPerPageLikes1DView,
  makeMetaCostPerPageLikes7DClick,
  makeMetaCostPerPageLikes7DClick1DView,
  makeMetaCostPerPageLikesDefault,
  makeMetaCostPerPostEngagement1DClick,
  makeMetaCostPerPostEngagement1DView,
  makeMetaCostPerPostEngagement7DClick,
  makeMetaCostPerPostEngagement7DClick1DView,
  makeMetaCostPerPostEngagementDefault,
  makeMetaCostPerThruplay1DView,
  makeMetaCostPerThruplay7DClick1DView,
  makeMetaCostPerView1DView,
  makeMetaCostPerView7DClick1DView,
  makeMetaHoldRate1DView,
  makeMetaHoldRate7DClick1DView,
  makeMetaLinkClicks,
  makeMetaMobileAppPurchases1DClick,
  makeMetaMobileAppPurchases1DView,
  makeMetaMobileAppPurchases7DClick,
  makeMetaMobileAppPurchases7DClick1DView,
  makeMetaMobileAppRevenue1DClick,
  makeMetaMobileAppRevenue1DView,
  makeMetaMobileAppRevenue7DClick,
  makeMetaMobileAppRevenue7DClick1DView,
  makeMetaOfflinePurchases1DClick,
  makeMetaOfflinePurchases1DView,
  makeMetaOfflinePurchases7DClick,
  makeMetaOfflinePurchases7DClick1DView,
  makeMetaOfflineRevenue1DClick,
  makeMetaOfflineRevenue1DView,
  makeMetaOfflineRevenue7DClick,
  makeMetaOfflineRevenue7DClick1DView,
  makeMetaOffsitePixelPurchases1DClick,
  makeMetaOffsitePixelPurchases1DView,
  makeMetaOffsitePixelPurchases7DClick,
  makeMetaOffsitePixelPurchases7DClick1DView,
  makeMetaOffsitePixelRevenue1DClick,
  makeMetaOffsitePixelRevenue1DView,
  makeMetaOffsitePixelRevenue7DClick,
  makeMetaOffsitePixelRevenue7DClick1DView,
  makeMetaOutboundClicks,
  makeMetaPageEngagement1DClick,
  makeMetaPageEngagement1DView,
  makeMetaPageEngagement7DClick,
  makeMetaPageEngagement7DClick1DView,
  makeMetaPageEngagementDefault,
  makeMetaPageLikes1DClick,
  makeMetaPageLikes1DView,
  makeMetaPageLikes7DClick,
  makeMetaPageLikes7DClick1DView,
  makeMetaPageLikesDefault,
  makeMetaPostEngagement1DClick,
  makeMetaPostEngagement1DView,
  makeMetaPostEngagement7DClick,
  makeMetaPostEngagement7DClick1DView,
  makeMetaPostEngagementDefault,
  makeMetaROAS1DClick,
  makeMetaROAS1DView,
  makeMetaROAS7DClick,
  makeMetaROAS7DClick1DView,
  makeMetaROASMetaShopRevenueIncluded1DClick,
  makeMetaROASMetaShopRevenueIncluded1DView,
  makeMetaROASMetaShopRevenueIncluded7DClick,
  makeMetaROASMetaShopRevenueIncluded7DClick1DView,
  makeMetaShopPurchases1DClick,
  makeMetaShopPurchases1DView,
  makeMetaShopPurchases7DClick,
  makeMetaShopPurchases7DClick1DView,
  makeMetaShopRevenue1DClick,
  makeMetaShopRevenue1DView,
  makeMetaShopRevenue7DClick,
  makeMetaShopRevenue7DClick1DView,
  makeMetaShopRevenuePlusNBAttributedRevenue1DClick,
  makeMetaShopRevenuePlusNBAttributedRevenue1DView,
  makeMetaShopRevenuePlusNBAttributedRevenue7DClick,
  makeMetaShopRevenuePlusNBAttributedRevenue7DClick1DView,
  makeMetaShopTransactionsPlusNBTransactions1DClick,
  makeMetaShopTransactionsPlusNBTransactions1DView,
  makeMetaShopTransactionsPlusNBTransactions7DClick,
  makeMetaShopTransactionsPlusNBTransactions7DClick1DView,
  makeMetaThruplayRate1DView,
  makeMetaThruplayRate7DClick1DView,
  makeMetaThumbstopRateDefault,
  makeMetaTotalPurchases1DClick,
  makeMetaTotalPurchases1DView,
  makeMetaTotalPurchases7DClick,
  makeMetaTotalPurchases7DClick1DView,
  makeMetaTotalRevenue1DClick,
  makeMetaTotalRevenue1DView,
  makeMetaTotalRevenue7DClick,
  makeMetaTotalRevenue7DClick1DView,
  makeMetaVideoP100Watched,
  makeMetaVideoP25Watched,
  makeMetaVideoP50Watched,
  makeMetaVideoP75Watched,
  makeMetaVideoP95Watched,
  makeMetaVideoPlayActions1DView,
  makeMetaVideoPlayActions7DClick1DView,
  makeMetaVideoThruplayWatchedActions1DView,
  makeMetaVideoThruplayWatchedActions7DClick1DView,
} from "./meta";
import {
  makeAov,
  makeAovFt,
  makeAovFtLtv,
  makeAovLtv,
  makeAovRtn,
  makeAovRtnLtv,
  makeAvgTouchpointsPerOrder,
  makeAvgTouchpointsPerOrderLtv,
  makeCac,
  makeCacFt,
  makeCacFtLtv,
  makeCacLtv,
  makeCacOrderSubscription,
  makeCacOrderSubscriptionFirstTime,
  makeCacOrderSubscriptionFirstTimeLtv,
  makeCacOrderSubscriptionLtv,
  makeCacOrderSubscriptionOther,
  makeCacOrderSubscriptionOtherLtv,
  makeCacOrderSubscriptionRecurring,
  makeCacOrderSubscriptionRecurringLtv,
  makeCacRtn,
  makeCacRtnLtv,
  makeCpm,
  makeNewCac,
  makeNewCacFt,
  makeNewCacRtn,
  makePercentageNewCustomers,
  makePercentageNewVisitors,
  makeCPV,
  makeCPVFt,
  makeCPVRtn,
  makeRPV,
  makeRPVFt,
  makeRPVRtn,
  makeCtr,
  makeCustomers,
  makeCustomersFt,
  makeCustomersFtRtn,
  makeCustomersRtn,
  makeCustomGoal,
  makeCustomGoalCost,
  makeCustomGoalCostLtv,
  makeCustomGoalLtv,
  makeCustomGoalRate,
  makeCustomGoalRateLtv,
  makeCustomMetric,
  makeEcpc,
  makeEcpnv,
  makeEcr,
  makeEcrFt,
  makeEcrFtLtv,
  makeEcrLtv,
  makeEcrRtn,
  makeEcrRtnLtv,
  makeForecastedRev30,
  makeForecastedRev60,
  makeForecastedRev90,
  makeForecastedRoas30,
  makeForecastedRoas60,
  makeForecastedRoas90,
  makeForecastedTxns30,
  makeForecastedTxns60,
  makeForecastedTxns90,
  makeImprs,
  makeNewVisits,
  makeOrderDiscounts,
  makeOrderDiscountsFt,
  makeOrderDiscountsFtLtv,
  makeOrderDiscountsLtv,
  makeOrderDiscountsOrderSubscriptions,
  makeOrderDiscountsOrderSubscriptionsFirstTime,
  makeOrderDiscountsOrderSubscriptionsFirstTimeLtv,
  makeOrderDiscountsOrderSubscriptionsLtv,
  makeOrderDiscountsOrderSubscriptionsOther,
  makeOrderDiscountsOrderSubscriptionsOtherLtv,
  makeOrderDiscountsOrderSubscriptionsRecurring,
  makeOrderDiscountsOrderSubscriptionsRecurringLtv,
  makeOrderDiscountsRtn,
  makeOrderDiscountsRtnLtv,
  makeOrderRefundedValue,
  makeOrderRefundedValueFt,
  makeOrderRefundedValueFtLtv,
  makeOrderRefundedValueLtv,
  makeOrderRefundedValueOrderSubscriptions,
  makeOrderRefundedValueOrderSubscriptionsFirstTime,
  makeOrderRefundedValueOrderSubscriptionsFirstTimeLtv,
  makeOrderRefundedValueOrderSubscriptionsLtv,
  makeOrderRefundedValueOrderSubscriptionsOther,
  makeOrderRefundedValueOrderSubscriptionsOtherLtv,
  makeOrderRefundedValueOrderSubscriptionsRecurring,
  makeOrderRefundedValueOrderSubscriptionsRecurringLtv,
  makeOrderRefundedValueRtn,
  makeOrderRefundedValueRtnLtv,
  makePercentageNewVisits,
  makeRev,
  makeRevenueMetaShops,
  makeRevenueMetaShopsLtv,
  makeRevenueOrderSubscription,
  makeRevenueOrderSubscriptionFirstTime,
  makeRevenueOrderSubscriptionFirstTimeLtv,
  makeRevenueOrderSubscriptionLtv,
  makeRevenueOrderSubscriptionOther,
  makeRevenueOrderSubscriptionOtherLtv,
  makeRevenueOrderSubscriptionRecurring,
  makeRevenueOrderSubscriptionRecurringLtv,
  makeRevenueTikTokShops,
  makeRevenueTikTokShopsLtv,
  makeRevenueWebShops,
  makeRevenueWebShopsLtv,
  makeRevFt,
  makeRevFtLtv,
  makeRevLtv,
  makeRevRtn,
  makeRevRtnLtv,
  makeRoas,
  makeRoasFt,
  makeRoasFtLtv,
  makeRoasLtv,
  makeRoasRtn,
  makeRoasRtnLtv,
  makeShippingValue,
  makeShippingValueFt,
  makeShippingValueFtLtv,
  makeShippingValueLtv,
  makeShippingValueOrderSubscriptions,
  makeShippingValueOrderSubscriptionsFirstTime,
  makeShippingValueOrderSubscriptionsFirstTimeLtv,
  makeShippingValueOrderSubscriptionsLtv,
  makeShippingValueOrderSubscriptionsOther,
  makeShippingValueOrderSubscriptionsOtherLtv,
  makeShippingValueOrderSubscriptionsRecurring,
  makeShippingValueOrderSubscriptionsRecurringLtv,
  makeShippingValueRtn,
  makeShippingValueRtnLtv,
  makeSpend,
  makeTaxValue,
  makeTaxValueFt,
  makeTaxValueFtLtv,
  makeTaxValueLtv,
  makeTaxValueOrderSubscriptions,
  makeTaxValueOrderSubscriptionsFirstTime,
  makeTaxValueOrderSubscriptionsFirstTimeLtv,
  makeTaxValueOrderSubscriptionsLtv,
  makeTaxValueOrderSubscriptionsOther,
  makeTaxValueOrderSubscriptionsOtherLtv,
  makeTaxValueOrderSubscriptionsRecurring,
  makeTaxValueOrderSubscriptionsRecurringLtv,
  makeTaxValueRtn,
  makeTaxValueRtnLtv,
  makeTransactionsMetaShops,
  makeTransactionsMetaShopsLtv,
  makeTransactionsOrderSubscription,
  makeTransactionsOrderSubscriptionFirstTime,
  makeTransactionsOrderSubscriptionFirstTimeLtv,
  makeTransactionsOrderSubscriptionLtv,
  makeTransactionsOrderSubscriptionOther,
  makeTransactionsOrderSubscriptionOtherLtv,
  makeTransactionsOrderSubscriptionRecurring,
  makeTransactionsOrderSubscriptionRecurringLtv,
  makeTransactionsTikTokShops,
  makeTransactionsTikTokShopsLtv,
  makeTransactionsWebShops,
  makeTransactionsWebShopsLtv,
  makeTxns,
  makeTxnsFt,
  makeTxnsFtLtv,
  makeTxnsLtv,
  makeTxnsRtn,
  makeTxnsRtnLtv,
  makeVisitors,
  makeVisitorsFt,
  makeVisitorsFtRtn,
  makeVisitorsRtn,
  makeVisits,
} from "./northbeam";
import {
  makePinterestAddToCartRate,
  makePinterestCAC,
  makePinterestCheckoutConversions,
  makePinterestCheckoutOrderValue,
  makePinterestPaid3SecVideoViews,
  makePinterestPaidEngagements,
  makePinterestPaidIdeaPinProductTagVisit,
  makePinterestPaidImpressions,
  makePinterestPaidPinClicks,
  makePinterestPaidSaveRate,
  makePinterestPaidSaves,
  makePinterestPaidVideoPlayedAt100,
  makePinterestPaidVideoPlayedAt25,
  makePinterestPaidVideoPlayedAt50,
  makePinterestPaidVideoPlayedAt75,
  makePinterestPaidVideoPlayedAt95,
  makePinterestPaidVideoStarts,
  makePinterestPaidVideoViews,
  makePinterestROAS,
} from "./pinterest";
import {
  makeSnapchatAttachmentTotalViewTimeMillis,
  makeSnapchatCAC,
  makeSnapchatEarnedImpressions,
  makeSnapchatPaidImpressions,
  makeSnapchatProfileClicks,
  makeSnapchatQuartile1,
  makeSnapchatQuartile2,
  makeSnapchatQuartile3,
  makeSnapchatRevenue,
  makeSnapchatROAS,
  makeSnapchatSwipes,
  makeSnapchatSwipesRate,
  makeSnapchatThumbstopRate,
  makeSnapchatTotalImpressions,
  makeSnapchatTransactions,
  makeSnapchatVideoViews,
  makeSnapchatVideoViews15S,
  makeSnapchatVideoViewsTimeBased,
  makeSnapchatViewCompletion,
} from "./snapchat";
import {
  makeTiktokCAC,
  makeTiktokClicks,
  makeTiktokCompletePayment,
  makeTiktokCPC,
  makeTiktokCPM,
  makeTiktokCTR,
  makeTiktokEngagements,
  makeTiktokGMVCostPer10SecondLiveView,
  makeTiktokGMVCostPerLiveView,
  makeTiktokGMVCostPerOrder,
  makeTiktokGMVGrossRevenue,
  makeTiktokGMVLiveFollows,
  makeTiktokGMVLiveViews,
  makeTiktokGMVNetCost,
  makeTiktokGMVOrders,
  makeTiktokGMVTenSecondLiveViews,
  makeTiktokImprs,
  makeTiktokLikes,
  makeTiktokROAS,
  makeTiktokThumbstopRate,
  makeTiktokTotalCompletePayment,
  makeTiktokVideoViews,
  makeTiktokVideoViews2s,
  makeTiktokVideoViews6s,
  makeTiktokVideoViewsP100,
  makeTiktokVideoViewsP25,
  makeTiktokVideoViewsP50,
  makeTiktokVideoViewsP75,
} from "./tiktok";
import { makeTradedeskImpressions } from "./tradedesk";
import {
  makeTwitterAppClicks,
  makeTwitterClicks,
  makeTwitterEngagements,
  makeTwitterFollows,
  makeTwitterLikes,
  makeTwitterReplies,
  makeTwitterRetweets,
  makeTwitterUnfollows,
  makeTwitterUrlClicks,
} from "./twitter";
import { type SalesMetricV2 } from "./types";

const TOUCHPOINTS_UNSUPPORTED_ATTRIBUTION_MODELS = [
  "northbeam_custom__va",
  "northbeam_custom_influencers__va",
  "northbeam_custom__enh",
  "northbeam_custom_matchbacks",
];

export function getMetrics({
  user,
  attributionModel,
  attributionWindow,
  accountingMode,
  timeGranularity,
  showV1Forecasts,
  customGoalRows,
  customMetricRows,
  doAnon = false,
  includeAllCustomMetrics = false,
  isDashboardV2 = true,
}: {
  user: AbstractUser;
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  accountingMode: INBAccountingMode;
  timeGranularity: ITimeGranularityV2;
  showV1Forecasts: boolean;
  customGoalRows: CustomGoalRow[];
  customMetricRows: GetCustomMetrics_me_customMetrics[];
  doAnon?: boolean;
  includeAllCustomMetrics?: boolean;
  isDashboardV2?: boolean;
}): SalesMetricV2[] {
  const includeV1Forecasts =
    showV1Forecasts &&
    timeGranularity !== "hourly" &&
    attributionModel === "northbeam_custom" &&
    accountingMode === "accrual";

  const attributionModelLabel =
    user.attributionMethodEnum.getLabel(attributionModel);

  const metrics: SalesMetricV2[] = [
    makeSpend(),
    makeImprs({ isDashboardV2 }),
    makeVisits(),
    makeNewVisits(),
    makeRev({
      attributionModelLabel,
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeRevFt({
      attributionModelLabel,
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeRevRtn({
      attributionModelLabel,
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeTxns({
      attributionModelLabel,
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeTxnsFt({
      attributionModelLabel,
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeTxnsRtn({
      attributionModelLabel,
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeRevenueWebShops({ accountingMode, attributionWindow }),
    makeRevenueMetaShops({ accountingMode, attributionWindow }),
    makeRevenueTikTokShops({ accountingMode, attributionWindow }),
    makeTransactionsWebShops({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeTransactionsMetaShops({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeTransactionsTikTokShops({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeOrderDiscounts({ accountingMode, attributionWindow }),
    makeOrderDiscountsFt({ accountingMode, attributionWindow, isDashboardV2 }),
    makeOrderDiscountsRtn({ accountingMode, attributionWindow }),
    makeTaxValue({ accountingMode, attributionWindow }),
    makeTaxValueFt({ accountingMode, attributionWindow, isDashboardV2 }),
    makeTaxValueRtn({ accountingMode, attributionWindow }),
    makeShippingValue({ accountingMode, attributionWindow }),
    makeShippingValueFt({ accountingMode, attributionWindow, isDashboardV2 }),
    makeShippingValueRtn({ accountingMode, attributionWindow }),
    makeOrderRefundedValue({ accountingMode, attributionWindow }),
    makeOrderRefundedValueFt({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeOrderRefundedValueRtn({ accountingMode, attributionWindow }),
    makeRevenueOrderSubscription({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeRevenueOrderSubscriptionFirstTime({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeRevenueOrderSubscriptionRecurring({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeRevenueOrderSubscriptionOther({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeTransactionsOrderSubscription({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeTransactionsOrderSubscriptionFirstTime({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeTransactionsOrderSubscriptionRecurring({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeTransactionsOrderSubscriptionOther({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeOrderDiscountsOrderSubscriptions({ accountingMode, attributionWindow }),
    makeOrderDiscountsOrderSubscriptionsFirstTime({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeOrderDiscountsOrderSubscriptionsRecurring({
      accountingMode,
      attributionWindow,
    }),
    makeOrderDiscountsOrderSubscriptionsOther({
      accountingMode,
      attributionWindow,
    }),
    makeTaxValueOrderSubscriptions({ accountingMode, attributionWindow }),
    makeTaxValueOrderSubscriptionsFirstTime({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeTaxValueOrderSubscriptionsRecurring({
      accountingMode,
      attributionWindow,
    }),
    makeTaxValueOrderSubscriptionsOther({ accountingMode, attributionWindow }),
    makeShippingValueOrderSubscriptions({ accountingMode, attributionWindow }),
    makeShippingValueOrderSubscriptionsFirstTime({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeShippingValueOrderSubscriptionsRecurring({
      accountingMode,
      attributionWindow,
    }),
    makeShippingValueOrderSubscriptionsOther({
      accountingMode,
      attributionWindow,
    }),
    makeOrderRefundedValueOrderSubscriptions({
      accountingMode,
      attributionWindow,
    }),
    makeOrderRefundedValueOrderSubscriptionsFirstTime({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeOrderRefundedValueOrderSubscriptionsRecurring({
      accountingMode,
      attributionWindow,
    }),
    makeOrderRefundedValueOrderSubscriptionsOther({
      accountingMode,
      attributionWindow,
    }),
    makeCacOrderSubscription({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeCacOrderSubscriptionFirstTime({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeCacOrderSubscriptionRecurring({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeCacOrderSubscriptionOther({
      accountingMode,
      attributionWindow,
      isDashboardV2,
    }),
    makeRoas({ accountingMode, attributionWindow, isDashboardV2 }),
    makeRoasFt({ accountingMode, attributionWindow, isDashboardV2 }),
    makeRoasRtn({ accountingMode, attributionWindow, isDashboardV2 }),
    makeCac({ accountingMode, attributionWindow, isDashboardV2 }),
    makeCacFt({ accountingMode, attributionWindow, isDashboardV2 }),
    makeCacRtn({ accountingMode, attributionWindow, isDashboardV2 }),
    makeAov({ accountingMode, attributionWindow }),
    makeAovFt({ accountingMode, attributionWindow }),
    makeAovRtn({ accountingMode, attributionWindow }),
    makeEcr({ accountingMode, attributionWindow }),
    makeEcrFt({ accountingMode, attributionWindow }),
    makeEcrRtn({ accountingMode, attributionWindow }),
    makePercentageNewVisits(),
    makeCpm(),
    makeCtr(),
    makeEcpc(),
    makeEcpnv(),
  ];

  if (timeGranularity !== "hourly") {
    metrics.push(
      makeVisitors({ accountingMode, attributionWindow }),
      makeVisitorsFt({ accountingMode, attributionWindow }),
      makeVisitorsFtRtn({ accountingMode, attributionWindow }),
      makeVisitorsRtn({ accountingMode, attributionWindow }),
      makePercentageNewVisitors(),
      makeCPV({ accountingMode, attributionWindow }),
      makeCPVFt({ accountingMode, attributionWindow }),
      makeCPVRtn({ accountingMode, attributionWindow }),
      makeRPV({ accountingMode, attributionWindow }),
      makeRPVFt({ accountingMode, attributionWindow }),
      makeRPVRtn({ accountingMode, attributionWindow }),
    );
  }

  if (
    timeGranularity !== "hourly" &&
    !TOUCHPOINTS_UNSUPPORTED_ATTRIBUTION_MODELS.includes(attributionModel)
  ) {
    metrics.push(
      makeAvgTouchpointsPerOrder({ accountingMode, attributionWindow }),
      makeAvgTouchpointsPerOrderLtv(),
      makeCustomers({ accountingMode, attributionWindow }),
      makeCustomersFt({ accountingMode, attributionWindow }),
      makeCustomersFtRtn({ accountingMode, attributionWindow }),
      makeCustomersRtn({ accountingMode, attributionWindow }),
      makePercentageNewCustomers(),
      makeNewCac({ accountingMode, attributionWindow }),
      makeNewCacFt({ accountingMode, attributionWindow }),
      makeNewCacRtn({ accountingMode, attributionWindow }),
    );
  }

  if (accountingMode === "accrual" && timeGranularity !== "hourly") {
    metrics.push(
      makeRevLtv({ attributionModelLabel, isDashboardV2 }),
      makeRevFtLtv({ attributionModelLabel, isDashboardV2 }),
      makeRevRtnLtv({ attributionModelLabel, isDashboardV2 }),
      makeTxnsLtv({ attributionModelLabel, isDashboardV2 }),
      makeTxnsFtLtv({ attributionModelLabel, isDashboardV2 }),
      makeTxnsRtnLtv({ attributionModelLabel, isDashboardV2 }),
      makeRevenueWebShopsLtv({ isDashboardV2 }),
      makeRevenueMetaShopsLtv({ isDashboardV2 }),
      makeRevenueTikTokShopsLtv({ isDashboardV2 }),
      makeTransactionsWebShopsLtv({ isDashboardV2 }),
      makeTransactionsMetaShopsLtv({ isDashboardV2 }),
      makeTransactionsTikTokShopsLtv({ isDashboardV2 }),
      makeOrderDiscountsLtv({ isDashboardV2 }),
      makeOrderDiscountsFtLtv({ isDashboardV2 }),
      makeOrderDiscountsRtnLtv({ isDashboardV2 }),
      makeTaxValueLtv({ isDashboardV2 }),
      makeTaxValueFtLtv({ isDashboardV2 }),
      makeTaxValueRtnLtv({ isDashboardV2 }),
      makeShippingValueLtv({ isDashboardV2 }),
      makeShippingValueFtLtv({ isDashboardV2 }),
      makeShippingValueRtnLtv({ isDashboardV2 }),
      makeOrderRefundedValueLtv({ isDashboardV2 }),
      makeOrderRefundedValueFtLtv({ isDashboardV2 }),
      makeOrderRefundedValueRtnLtv({ isDashboardV2 }),
      makeRevenueOrderSubscriptionLtv({ isDashboardV2 }),
      makeRevenueOrderSubscriptionFirstTimeLtv({ isDashboardV2 }),
      makeRevenueOrderSubscriptionRecurringLtv({ isDashboardV2 }),
      makeRevenueOrderSubscriptionOtherLtv({ isDashboardV2 }),
      makeTransactionsOrderSubscriptionLtv({ isDashboardV2 }),
      makeTransactionsOrderSubscriptionFirstTimeLtv({ isDashboardV2 }),
      makeTransactionsOrderSubscriptionRecurringLtv({ isDashboardV2 }),
      makeTransactionsOrderSubscriptionOtherLtv({ isDashboardV2 }),
      makeOrderDiscountsOrderSubscriptionsLtv({ isDashboardV2 }),
      makeOrderDiscountsOrderSubscriptionsFirstTimeLtv({ isDashboardV2 }),
      makeOrderDiscountsOrderSubscriptionsRecurringLtv({ isDashboardV2 }),
      makeOrderDiscountsOrderSubscriptionsOtherLtv({ isDashboardV2 }),
      makeTaxValueOrderSubscriptionsLtv({ isDashboardV2 }),
      makeTaxValueOrderSubscriptionsFirstTimeLtv({ isDashboardV2 }),
      makeTaxValueOrderSubscriptionsRecurringLtv({ isDashboardV2 }),
      makeTaxValueOrderSubscriptionsOtherLtv({ isDashboardV2 }),
      makeShippingValueOrderSubscriptionsLtv({ isDashboardV2 }),
      makeShippingValueOrderSubscriptionsFirstTimeLtv({ isDashboardV2 }),
      makeShippingValueOrderSubscriptionsRecurringLtv({ isDashboardV2 }),
      makeShippingValueOrderSubscriptionsOtherLtv({ isDashboardV2 }),
      makeOrderRefundedValueOrderSubscriptionsLtv({ isDashboardV2 }),
      makeOrderRefundedValueOrderSubscriptionsFirstTimeLtv({ isDashboardV2 }),
      makeOrderRefundedValueOrderSubscriptionsRecurringLtv({ isDashboardV2 }),
      makeOrderRefundedValueOrderSubscriptionsOtherLtv({ isDashboardV2 }),
      makeCacOrderSubscriptionLtv({ isDashboardV2 }),
      makeCacOrderSubscriptionFirstTimeLtv({ isDashboardV2 }),
      makeCacOrderSubscriptionRecurringLtv({ isDashboardV2 }),
      makeCacOrderSubscriptionOtherLtv({ isDashboardV2 }),
      makeRoasLtv({ isDashboardV2 }),
      makeRoasFtLtv({ isDashboardV2 }),
      makeRoasRtnLtv({ isDashboardV2 }),
      makeCacLtv({ isDashboardV2 }),
      makeCacFtLtv({ isDashboardV2 }),
      makeCacRtnLtv({ isDashboardV2 }),
      makeAovLtv({ isDashboardV2 }),
      makeAovFtLtv({ isDashboardV2 }),
      makeAovRtnLtv({ isDashboardV2 }),
      makeEcrLtv({ isDashboardV2 }),
      makeEcrFtLtv({ isDashboardV2 }),
      makeEcrRtnLtv({ isDashboardV2 }),
    );
  }

  if (includeV1Forecasts) {
    if (
      attributionWindow !== "30" &&
      attributionWindow !== "60" &&
      attributionWindow !== "90"
    ) {
      metrics.push(
        makeForecastedRev30({ attributionModelLabel, isDashboardV2 }),
        makeForecastedTxns30({ attributionModelLabel, isDashboardV2 }),
        makeForecastedRoas30(),
      );
    }

    if (attributionWindow !== "60" && attributionWindow !== "90") {
      metrics.push(
        makeForecastedRev60({ attributionModelLabel, isDashboardV2 }),
        makeForecastedTxns60({ attributionModelLabel, isDashboardV2 }),
        makeForecastedRoas60(),
      );
    }

    if (attributionWindow !== "90") {
      metrics.push(
        makeForecastedRev90({ attributionModelLabel, isDashboardV2 }),
        makeForecastedTxns90({ attributionModelLabel, isDashboardV2 }),
        makeForecastedRoas90(),
      );
    }
  }

  for (const goalRow of customGoalRows) {
    metrics.push(
      makeCustomGoal({
        goalId: goalRow.id,
        goalName: goalRow.displayName,
        accountingMode,
        attributionModelLabel,
        attributionWindow,
      }),
      makeCustomGoalCost({
        goalId: goalRow.id,
        goalName: goalRow.displayName,
        accountingMode,
        attributionWindow,
      }),
      makeCustomGoalRate({
        goalId: goalRow.id,
        goalName: goalRow.displayName,
        accountingMode,
        attributionWindow,
      }),
    );

    if (accountingMode === "accrual" && timeGranularity !== "hourly") {
      metrics.push(
        makeCustomGoalLtv({
          goalId: goalRow.id,
          goalName: goalRow.displayName,
          attributionModelLabel,
          isDashboardV2,
        }),
        makeCustomGoalCostLtv({
          goalId: goalRow.id,
          goalName: goalRow.displayName,
          isDashboardV2,
        }),
        makeCustomGoalRateLtv({
          goalId: goalRow.id,
          goalName: goalRow.displayName,
          isDashboardV2,
        }),
      );
    }
  }

  for (const metricRow of customMetricRows) {
    if (
      includeAllCustomMetrics ||
      metricRow.accounting_mode === accountingMode
    ) {
      metrics.push(makeCustomMetric({ customMetric: metricRow }));
    }
  }

  if (user.featureFlags.enableGooglePlatformMetrics) {
    metrics.push(
      makeGoogleAllConversions(),
      makeGoogleConversions(),
      makeGoogleAllConversionsValue(),
      makeGoogleConversionsValue(),
      makeGoogleVideoViews(),
      makeGoogleClicks(),
      makeGoogleImpressions(),
      makeGoogleViewThroughConversions(),
      makeGoogleROAS(),
      makeGoogleCAC(),
      makeGoogleCostPerConversion(),
      makeGoogleAverageCPC(),
    );
  }

  if (user.featureFlags.enableTikTokPlatformMetrics) {
    metrics.push(
      makeTiktokVideoViews(),
      makeTiktokVideoViews2s(),
      makeTiktokVideoViews6s(),
      makeTiktokVideoViewsP100(),
      makeTiktokVideoViewsP75(),
      makeTiktokVideoViewsP50(),
      makeTiktokVideoViewsP25(),
      makeTiktokImprs(),
      makeTiktokClicks(),
      makeTiktokLikes(),
      makeTiktokCompletePayment(),
      makeTiktokTotalCompletePayment(),
      makeTiktokEngagements(),
      makeTiktokCPM(),
      makeTiktokCTR(),
      makeTiktokCPC(),
      makeTiktokROAS(),
      makeTiktokCAC(),
      makeTiktokThumbstopRate(),
    );
  }

  if (user.featureFlags.enableTikTokGMVMetrics) {
    metrics.push(
      makeTiktokGMVNetCost(),
      makeTiktokGMVGrossRevenue(),
      makeTiktokGMVCostPerLiveView(),
      makeTiktokGMVCostPer10SecondLiveView(),
      makeTiktokGMVOrders(),
      makeTiktokGMVLiveViews(),
      makeTiktokGMVTenSecondLiveViews(),
      makeTiktokGMVLiveFollows(),
      makeTiktokGMVCostPerOrder(),
    );
  }

  if (user.featureFlags.enablePinterestPlatformMetrics) {
    metrics.push(
      makePinterestCheckoutConversions(),
      makePinterestCheckoutOrderValue(),
      makePinterestAddToCartRate(),
      makePinterestPaidImpressions(),
      makePinterestPaidEngagements(),
      makePinterestPaidPinClicks(),
      makePinterestPaidSaveRate(),
      makePinterestPaidSaves(),
      makePinterestPaidVideoViews(),
      makePinterestPaid3SecVideoViews(),
      makePinterestPaidVideoPlayedAt50(),
      makePinterestPaidVideoPlayedAt95(),
      makePinterestPaidVideoPlayedAt75(),
      makePinterestPaidVideoPlayedAt100(),
      makePinterestPaidVideoPlayedAt25(),
      makePinterestPaidVideoStarts(),
      makePinterestPaidIdeaPinProductTagVisit(),
      makePinterestROAS(),
      makePinterestCAC(),
    );
  }

  if (user.featureFlags.enableSnapchatPlatformMetrics) {
    metrics.push(
      makeSnapchatPaidImpressions(),
      makeSnapchatEarnedImpressions(),
      makeSnapchatTotalImpressions(),
      makeSnapchatSwipes(),
      makeSnapchatSwipesRate(),
      makeSnapchatThumbstopRate(),
      makeSnapchatProfileClicks(),
      makeSnapchatVideoViews(),
      makeSnapchatVideoViewsTimeBased(),
      makeSnapchatVideoViews15S(),
      makeSnapchatQuartile1(),
      makeSnapchatQuartile2(),
      makeSnapchatQuartile3(),
      makeSnapchatViewCompletion(),
      makeSnapchatAttachmentTotalViewTimeMillis(),
      makeSnapchatTransactions(),
      makeSnapchatRevenue(),
      makeSnapchatROAS(),
      makeSnapchatCAC(),
    );
  }

  if (user.featureFlags.enableTwitterPlatformMetrics) {
    metrics.push(
      makeTwitterClicks(),
      makeTwitterAppClicks(),
      makeTwitterLikes(),
      makeTwitterFollows(),
      makeTwitterRetweets(),
      makeTwitterUnfollows(),
      makeTwitterEngagements(),
      makeTwitterReplies(),
      makeTwitterUrlClicks(),
    );
  }

  if (user.featureFlags.enableTradedeskPlatformMetrics) {
    metrics.push(makeTradedeskImpressions());
  }

  if (!user.featureFlags.disableFBPlatformPerformanceMetrics) {
    metrics.push(
      makeMetaTotalPurchases1DClick({ isDashboardV2 }),
      makeMetaTotalPurchases1DView({ isDashboardV2 }),
      makeMetaTotalPurchases7DClick({ isDashboardV2 }),
      makeMetaTotalPurchases7DClick1DView({ isDashboardV2 }),

      makeMetaShopPurchases1DView({ isDashboardV2 }),
      makeMetaShopPurchases1DClick({ isDashboardV2 }),
      makeMetaShopPurchases7DClick({ isDashboardV2 }),
      makeMetaShopPurchases7DClick1DView({ isDashboardV2 }),

      makeMetaOffsitePixelPurchases1DClick({ isDashboardV2 }),
      makeMetaOffsitePixelPurchases7DClick({ isDashboardV2 }),
      makeMetaOffsitePixelPurchases7DClick1DView({ isDashboardV2 }),
      makeMetaOffsitePixelPurchases1DView({ isDashboardV2 }),

      makeMetaTotalRevenue1DView({ isDashboardV2 }),
      makeMetaTotalRevenue1DClick({ isDashboardV2 }),
      makeMetaTotalRevenue7DClick({ isDashboardV2 }),
      makeMetaTotalRevenue7DClick1DView({ isDashboardV2 }),

      makeMetaShopRevenue1DView({ isDashboardV2 }),
      makeMetaShopRevenue1DClick({ isDashboardV2 }),
      makeMetaShopRevenue7DClick({ isDashboardV2 }),
      makeMetaShopRevenue7DClick1DView({ isDashboardV2 }),

      makeMetaOffsitePixelRevenue1DView({ isDashboardV2 }),
      makeMetaOffsitePixelRevenue1DClick({ isDashboardV2 }),
      makeMetaOffsitePixelRevenue7DClick({ isDashboardV2 }),
      makeMetaOffsitePixelRevenue7DClick1DView({ isDashboardV2 }),

      makeMetaROAS1DView({ isDashboardV2 }),
      makeMetaROAS1DClick({ isDashboardV2 }),
      makeMetaROAS7DClick({ isDashboardV2 }),
      makeMetaROAS7DClick1DView({ isDashboardV2 }),

      makeMetaCAC1DView({ isDashboardV2 }),
      makeMetaCAC1DClick({ isDashboardV2 }),
      makeMetaCAC7DClick({ isDashboardV2 }),
      makeMetaCAC7DClick1DView({ isDashboardV2 }),

      makeMetaAOV1DView({ isDashboardV2 }),
      makeMetaAOV1DClick({ isDashboardV2 }),
      makeMetaAOV7DClick({ isDashboardV2 }),
      makeMetaAOV7DClick1DView({ isDashboardV2 }),

      makeMetaPostEngagement1DView({ isDashboardV2 }),
      makeMetaPostEngagement1DClick({ isDashboardV2 }),
      makeMetaPostEngagement7DClick({ isDashboardV2 }),
      makeMetaPostEngagement7DClick1DView({ isDashboardV2 }),
      makeMetaPostEngagementDefault({ isDashboardV2 }),
      makeMetaPageEngagement1DView({ isDashboardV2 }),
      makeMetaPageEngagement1DClick({ isDashboardV2 }),
      makeMetaPageEngagement7DClick({ isDashboardV2 }),
      makeMetaPageEngagement7DClick1DView({ isDashboardV2 }),
      makeMetaPageEngagementDefault({ isDashboardV2 }),
      makeMetaPageLikes1DView({ isDashboardV2 }),
      makeMetaPageLikes1DClick({ isDashboardV2 }),
      makeMetaPageLikes7DClick({ isDashboardV2 }),
      makeMetaPageLikes7DClick1DView({ isDashboardV2 }),
      makeMetaPageLikesDefault({ isDashboardV2 }),

      makeMetaCostPer3SecondVideoPlays({ isDashboardV2 }),

      makeMeta3SVideoViewsDefault({ isDashboardV2 }),
      makeMeta3SVideoViews1DClick({ isDashboardV2 }),
      makeMeta3SVideoViews7DClick({ isDashboardV2 }),
      makeMeta3SVideoViews7DClick1DView({ isDashboardV2 }),

      makeMetaCostPerPageEngagement1DView({ isDashboardV2 }),
      makeMetaCostPerPageEngagement1DClick({ isDashboardV2 }),
      makeMetaCostPerPageEngagement7DClick1DView({ isDashboardV2 }),
      makeMetaCostPerPageEngagement7DClick({ isDashboardV2 }),
      makeMetaCostPerPageEngagementDefault({ isDashboardV2 }),
      makeMetaCostPerPageLikes1DView({ isDashboardV2 }),
      makeMetaCostPerPageLikes1DClick({ isDashboardV2 }),
      makeMetaCostPerPageLikes7DClick({ isDashboardV2 }),
      makeMetaCostPerPageLikes7DClick1DView({ isDashboardV2 }),
      makeMetaCostPerPageLikesDefault({ isDashboardV2 }),
      makeMetaCostPerPostEngagement1DClick({ isDashboardV2 }),
      makeMetaCostPerPostEngagement1DView({ isDashboardV2 }),
      makeMetaCostPerPostEngagement7DClick({ isDashboardV2 }),
      makeMetaCostPerPostEngagement7DClick1DView({ isDashboardV2 }),
      makeMetaCostPerPostEngagementDefault({ isDashboardV2 }),
    );
  }

  if (user.featureFlags.enableFBOfflineAndMobileAppColumns) {
    metrics.push(
      makeMetaOfflineRevenue1DView({ isDashboardV2 }),
      makeMetaOfflineRevenue1DClick({ isDashboardV2 }),
      makeMetaOfflineRevenue7DClick({ isDashboardV2 }),
      makeMetaOfflineRevenue7DClick1DView({ isDashboardV2 }),
      makeMetaMobileAppRevenue1DView({ isDashboardV2 }),
      makeMetaMobileAppRevenue1DClick({ isDashboardV2 }),
      makeMetaMobileAppRevenue7DClick({ isDashboardV2 }),
      makeMetaMobileAppRevenue7DClick1DView({ isDashboardV2 }),
      makeMetaOfflinePurchases1DView({ isDashboardV2 }),
      makeMetaOfflinePurchases1DClick({ isDashboardV2 }),
      makeMetaOfflinePurchases7DClick({ isDashboardV2 }),
      makeMetaOfflinePurchases7DClick1DView({ isDashboardV2 }),
      makeMetaMobileAppPurchases1DView({ isDashboardV2 }),
      makeMetaMobileAppPurchases1DClick({ isDashboardV2 }),
      makeMetaMobileAppPurchases7DClick({ isDashboardV2 }),
      makeMetaMobileAppPurchases7DClick1DView({ isDashboardV2 }),
    );
  }

  if (
    timeGranularity !== "hourly" &&
    !user.featureFlags.disableFBPlatformVideoMetrics
  ) {
    metrics.push(
      makeMetaThumbstopRateDefault({ isDashboardV2 }),
      makeMetaHoldRate1DView({ isDashboardV2 }),
      makeMetaHoldRate7DClick1DView({ isDashboardV2 }),
      makeMetaVideoPlayActions1DView({ isDashboardV2 }),
      makeMetaVideoPlayActions7DClick1DView({ isDashboardV2 }),
      makeMetaCostPerView1DView({ isDashboardV2 }),
      makeMetaCostPerView7DClick1DView({ isDashboardV2 }),
      makeMetaVideoThruplayWatchedActions1DView({ isDashboardV2 }),
      makeMetaVideoThruplayWatchedActions7DClick1DView({ isDashboardV2 }),
      makeMetaThruplayRate1DView({ isDashboardV2 }),
      makeMetaThruplayRate7DClick1DView({ isDashboardV2 }),
      makeMetaCostPerThruplay1DView({ isDashboardV2 }),
      makeMetaCostPerThruplay7DClick1DView({ isDashboardV2 }),
      makeMetaVideoP25Watched({ isDashboardV2 }),
      makeMetaVideoP50Watched({ isDashboardV2 }),
      makeMetaVideoP75Watched({ isDashboardV2 }),
      makeMetaVideoP95Watched({ isDashboardV2 }),
      makeMetaVideoP100Watched({ isDashboardV2 }),
      makeMetaLinkClicks({ isDashboardV2 }),
      makeMetaOutboundClicks({ isDashboardV2 }),
    );
  }

  if (!user.featureFlags.disableFBCustomCalculationMetrics) {
    metrics.push(
      makeMetaShopRevenuePlusNBAttributedRevenue1DView({ isDashboardV2 }),
      makeMetaShopRevenuePlusNBAttributedRevenue1DClick({ isDashboardV2 }),
      makeMetaShopRevenuePlusNBAttributedRevenue7DClick({ isDashboardV2 }),
      makeMetaShopRevenuePlusNBAttributedRevenue7DClick1DView({
        isDashboardV2,
      }),
      makeMetaROASMetaShopRevenueIncluded1DView({ isDashboardV2 }),
      makeMetaROASMetaShopRevenueIncluded1DClick({ isDashboardV2 }),
      makeMetaROASMetaShopRevenueIncluded7DClick({ isDashboardV2 }),
      makeMetaROASMetaShopRevenueIncluded7DClick1DView({ isDashboardV2 }),
      makeMetaShopTransactionsPlusNBTransactions1DView({ isDashboardV2 }),
      makeMetaShopTransactionsPlusNBTransactions1DClick({ isDashboardV2 }),
      makeMetaShopTransactionsPlusNBTransactions7DClick({ isDashboardV2 }),
      makeMetaShopTransactionsPlusNBTransactions7DClick1DView({
        isDashboardV2,
      }),
      makeMetaCacWithMetaShopTransactionsIncluded1DView({ isDashboardV2 }),
      makeMetaCacWithMetaShopTransactionsIncluded1DClick({ isDashboardV2 }),
      makeMetaCacWithMetaShopTransactionsIncluded7DClick({ isDashboardV2 }),
      makeMetaCacWithMetaShopTransactionsIncluded7DClick1DView({
        isDashboardV2,
      }),
    );
  }

  return metrics.map((e) => ({
    ...e,
    getValue: doAnon
      ? (metrics: any) => getAnonValue(e.getValue(metrics))
      : e.getValue,
  }));
}

const SCALING_FACTOR = Math.random() * (10 - 0.1) + 0.3;

function getAnonValue(value: number | undefined) {
  if (!value) {
    return;
  }

  return value * SCALING_FACTOR;
}
